<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatText } from '@/helpers/format-text'
import { Head, useForm } from '@inertiajs/vue3'
import parsePhoneNumberFromString, { isValidPhoneNumber } from 'libphonenumber-js'
import { Camera, Loader2, Trash, User as UserIcon } from 'lucide-vue-next'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { User } from '@/@types'
import { Info } from '@/@types/info'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card } from '@/components/ui/card'
import CardContent from '@/components/ui/card/CardContent.vue'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Title } from '@/components/ui/title'
import { useZodValidation } from '@/composables/useZodValidation'
import OrderLayout from '@/layouts/OrderLayout.vue'
import { z } from 'zod'

const { t } = useI18n()

const { user_to_edit } = defineProps<{
    user_to_edit: User
    billing_setor: Info[]
    billing_area: Info[]
    billing_nivel_hierarquico: Info[]
}>()

const form = useForm({
    name: user_to_edit.name || '',
    first_name: user_to_edit.first_name || '',
    last_name: user_to_edit.last_name || '',
    billing_linkedin: user_to_edit.billing_linkedin || '',
    billing_empresa: user_to_edit.billing_empresa || '',
    billing_setor: user_to_edit.billing_setor || '',
    billing_nivel_hierarquico: user_to_edit.billing_nivel_hierarquico || '',
    billing_area: user_to_edit.billing_area || '',
    phone: user_to_edit.phone || '',
    has_avatar: user_to_edit.has_avatar || false,
    avatar: null as File | null,
})

const avatarPreview = ref<string | null>(user_to_edit.avatar_url || null)
const fileInput = ref<HTMLInputElement | null>(null)

function handleAvatarClick() {
    fileInput.value?.click()
}

function handleAvatarChange(event: Event) {
    const target = event.target as HTMLInputElement
    if (target.files && target.files[0]) {
        const file = target.files[0]

        if (!file.type.startsWith('image/')) {
            return
        }

        form.avatar = file
        form.has_avatar = true
        avatarPreview.value = URL.createObjectURL(file)
    }
}

const { validate } = useZodValidation(
    z.object({
        name: z
            .string()
            .min(1, { message: t('Required') })
            .max(150, { message: t('Max 150 characters') }),
        first_name: z
            .string()
            .min(1, { message: t('Required') })
            .max(150, { message: t('Max 150 characters') }),
        last_name: z
            .string()
            .min(1, { message: t('Required') })
            .max(150, { message: t('Max 150 characters') }),
        billing_empresa: z.string().min(1, { message: t('Required') }),
        billing_linkedin: z
            .string()
            .optional()
            .refine((value) => !value || /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})(\/[\w.-]*)*\/?$/.test(value), {
                message: t('Invalid URL'),
            }),
        billing_setor: z.string().min(1, { message: t('Required') }),
        billing_nivel_hierarquico: z.string().min(1, { message: t('Required') }),
        billing_area: z.string().min(1, { message: t('Required') }),
        phone: z
            .string()
            .min(1, { message: t('Required') })
            .refine(
                (value) => {
                    const phoneNumber = parsePhoneNumberFromString(value)

                    if (phoneNumber) {
                        return isValidPhoneNumber(phoneNumber.number, 'BR')
                    }

                    return false
                },
                { message: t('Invalid phone number') },
            ),
    }),
    form,
)

function handleSubmit() {
    if (!validate()) return

    form.transform((data) => ({
        user: {
            ...data,
        },
    })).put('/users', {
        preserveState: true,
        only: ['flash', 'errors', 'user'],
        forceFormData: true,
    })
}

function handleRemoveAvatar() {
    form.delete('/users/avatar', {
        preserveScroll: true,
        onSuccess: () => {
            avatarPreview.value = null
            form.avatar = null
        },
    })
}
</script>

<template>
    <Head :title="$t('Edit Profile')" />

    <OrderLayout>
        <main class="mx-auto flex max-w-4xl flex-col gap-8 p-4">
            <Title class="flex items-center gap-2 font-sans font-semibold text-white">{{ $t('Edit Profile') }}</Title>

            <Card class="">
                <CardContent>
                    <div class="mt-8 w-full">
                        <form @submit.prevent="handleSubmit" autocomplete="off" id="register" class="mt-8 grid gap-8 md:grid-cols-2">
                            <input type="text" name="fake_username" autocomplete="username" style="display: none" />

                            <div class="col-span-full flex flex-col items-center justify-center gap-4">
                                <div class="group relative cursor-pointer" @click="handleAvatarClick">
                                    <Avatar class="h-24 w-24">
                                        <AvatarImage :src="avatarPreview || ''" alt="Avatar" />
                                        <AvatarFallback>
                                            <UserIcon class="text-muted-foreground h-10 w-10" />
                                        </AvatarFallback>
                                    </Avatar>

                                    <div
                                        class="absolute inset-0 flex items-center justify-center rounded-full bg-black/60 opacity-0 transition-opacity group-hover:opacity-100"
                                    >
                                        <Camera class="h-8 w-8 text-white" />
                                    </div>
                                </div>

                                <div v-if="avatarPreview" class="mt-2">
                                    <AlertDialog>
                                        <AlertDialogTrigger as-child>
                                            <Button variant="ghost" size="sm" class="text-destructive hover:text-destructive/90 h-8">
                                                <Trash class="text-destructive mr-2 h-4 w-4" />
                                                {{ $t('Remove avatar') }}
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>{{ $t('Remove avatar') }}</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    {{ $t('Are you sure you want to remove your avatar? This action cannot be undone.') }}
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>{{ $t('Cancel') }}</AlertDialogCancel>
                                                <AlertDialogAction
                                                    @click="handleRemoveAvatar"
                                                    class="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                >
                                                    {{ $t('Remove') }}
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>

                                <input ref="fileInput" type="file" class="hidden" accept="image/*" @change="handleAvatarChange" />
                                <span class="text-muted-foreground text-sm">{{ $t('Click to change avatar') }}</span>
                                <p class="text-destructive text-xs" v-if="form.errors.avatar">{{ form.errors.avatar }}</p>
                            </div>

                            <div class="col-span-full">
                                <h3 class="text-base font-semibold">{{ $t('Personal data') }}</h3>
                            </div>

                            <div>
                                <Label required for="name">{{ $t('Display Name') }}</Label>
                                <Input
                                    id="name"
                                    type="name"
                                    :placeholder="$t('Display Name')"
                                    v-model="form.name"
                                    @input="(e: Event) => (form.name = formatText((e.target as HTMLInputElement).value))"
                                />
                                <p class="text-destructive text-xs" v-if="form.errors['name']">{{ form.errors['name'] }}</p>
                            </div>

                            <div>
                                <Label required for="first_name">{{ $t('First name') }}</Label>
                                <Input
                                    id="first_name"
                                    type="name"
                                    :placeholder="$t('First name')"
                                    v-model="form.first_name"
                                    @input="(e: Event) => (form.first_name = formatText((e.target as HTMLInputElement).value))"
                                />
                                <p class="text-destructive text-xs" v-if="form.errors['first_name']">{{ form.errors['first_name'] }}</p>
                            </div>

                            <div>
                                <Label required for="last_name">{{ $t('Last name') }}</Label>
                                <Input
                                    id="last_name"
                                    type="name"
                                    :placeholder="$t('Last name')"
                                    v-model="form.last_name"
                                    @input="(e: Event) => (form.last_name = formatText((e.target as HTMLInputElement).value))"
                                />
                                <p class="text-destructive text-xs" v-if="form.errors['last_name']">{{ form.errors['last_name'] }}</p>
                            </div>

                            <div>
                                <Label for="linkedin">{{ $t('Linkedin') }}</Label>
                                <Input id="linkedin" type="text" :placeholder="$t('Linkedin')" v-model="form.billing_linkedin" />
                                <p class="text-destructive text-xs" v-if="form.errors['billing_linkedin']">
                                    {{ form.errors['billing_linkedin'] }}
                                </p>
                            </div>

                            <div>
                                <Label required for="email">{{ $t('Email') }}</Label>

                                <Input id="email" type="name" :placeholder="$t('Email')" disabled v-model="user_to_edit.email" />
                                <small class="text-muted-foreground">{{ $t('Email cannot be edited') }}</small>
                            </div>

                            <div>
                                <Label required for="phone">{{ $t('Cell phone') }}</Label>
                                <Input id="phone" type="tel" :placeholder="$t('(00) 00000-0000')" v-model="form.phone" />
                                <p class="text-destructive text-xs" v-if="form.errors['phone']">{{ form.errors['phone'] }}</p>
                            </div>

                            <div>
                                <Label required for="company">{{ $t('Company') }}</Label>
                                <Input id="company" type="text" v-model="form.billing_empresa" :placeholder="$t('Enter the company name')" />
                                <p class="text-destructive text-xs" v-if="form.errors['billing_empresa']">
                                    {{ form.errors['billing_empresa'] }}
                                </p>
                            </div>

                            <div>
                                <Label required for="">{{ $t('Sector') }}</Label>

                                <Select v-model="form.billing_setor">
                                    <SelectTrigger class="w-full">
                                        <SelectValue :placeholder="$t('Select')" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem v-for="setor in billing_setor" :value="setor.slug">
                                            <template v-if="$i18n.locale === 'pt-BR'">
                                                {{ setor.name }}
                                            </template>
                                            <template v-else>
                                                {{ setor.name_en || setor.name }}
                                            </template>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                                <p class="text-destructive text-xs" v-if="form.errors['billing_setor']">
                                    {{ form.errors['billing_setor'] }}
                                </p>
                            </div>

                            <div>
                                <Label required for="">{{ $t('Hierarchical Level') }}</Label>

                                <Select v-model="form.billing_nivel_hierarquico">
                                    <SelectTrigger class="w-full">
                                        <SelectValue :placeholder="$t('Select')" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem v-for="setor in billing_nivel_hierarquico" :value="setor.slug">
                                            <template v-if="$i18n.locale === 'pt-BR'">
                                                {{ setor.name }}
                                            </template>
                                            <template v-else>
                                                {{ setor.name_en || setor.name }}
                                            </template>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                                <p class="text-destructive text-xs" v-if="form.errors['billing_nivel_hierarquico']">
                                    {{ form.errors['billing_nivel_hierarquico'] }}
                                </p>
                            </div>

                            <div>
                                <Label required for="">{{ $t('Area') }}</Label>

                                <Select v-model="form.billing_area">
                                    <SelectTrigger class="w-full">
                                        <SelectValue :placeholder="$t('Select')" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem v-for="setor in billing_area" :value="setor.slug">
                                            <template v-if="$i18n.locale === 'pt-BR'">
                                                {{ setor.name }}
                                            </template>
                                            <template v-else>
                                                {{ setor.name_en || setor.name }}
                                            </template>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                                <p class="text-destructive text-xs" v-if="form.errors['billing_area']">{{ form.errors['billing_area'] }}</p>
                            </div>

                            <div class="col-span-full">
                                {{ $t('By submitting this form, you declare that you have read and agree to our') }}
                                <a href="/privacy-policy#privacy">{{ $t('Privacy Policy') }}</a
                                >.
                            </div>

                            <div class="mflex w-full items-center justify-between">
                                <Button type="submit" :disabled="form.processing" class="grid place-items-center">
                                    <span v-if="!form.processing" class="col-start-1 row-start-1"> {{ $t('Save') }} </span>
                                    <span v-if="form.processing" class="col-start-1 row-start-1" :aria-label="$t('Saving')">
                                        <Loader2 class="animate-spin" />
                                    </span>
                                </Button>
                            </div>
                        </form>
                    </div>
                </CardContent>
            </Card>
        </main>
    </OrderLayout>
</template>
